import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Droplets, Eye, EyeOff } from "lucide-react";
import { useLocale } from "../context/LocaleContext";
import LanguageToggle from "../components/LanguageToggle";

export default function Login() {
  const { t } = useLocale();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // NOTE (security fix): this page used to have a "Crear cuenta" toggle
  // that called POST /api/auth/register directly, with no auth required.
  // That endpoint granted a working "operator" account (full access to
  // orders, customers, and payment data) to anyone who filled the form —
  // no invitation, no admin approval. It's now locked down on the
  // backend to first-run bootstrap only (see routes/auth_routes.py), and
  // the UI for it is removed here. Admins create staff accounts from
  // User Management (POST /api/admin/users, already used by
  // pages/UserManagement.jsx) instead.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedPolicies) {
      toast.error(t("You must accept the terms and privacy policy", "Debes aceptar los términos y la política de privacidad"));
      return;
    }
    setLoading(true);

    try {
      await login(email, password);
      toast.success(t("Welcome back!", "¡Bienvenido de vuelta!"));
      navigate("/admin");
    } catch (error) {
      const message = error.response?.data?.detail || t("An error occurred", "Ocurrió un error");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-sky-100 flex items-center justify-center">
                <Droplets className="h-7 w-7 text-sky-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Ventura Fresh</h1>
                <p className="text-sm text-slate-500">{t("Laundry CRM", "CRM de Lavandería")}</p>
              </div>
            </div>
            <LanguageToggle />
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              {t("Sign in", "Iniciar sesión")}
            </h2>
            <p className="text-slate-500">
              {t("Enter your credentials to access the panel", "Ingresa tus credenciales para acceder al panel")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700">{t("Email", "Correo electrónico")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("your@email.com", "tu@email.com")}
                className="mt-1.5 input-default"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">{t("Password", "Contraseña")}</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-default pr-10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-start gap-3" data-testid="login-acceptance">
              <input
                type="checkbox"
                checked={acceptedPolicies}
                onChange={(e) => setAcceptedPolicies(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600"
                data-testid="login-accept-checkbox"
              />
              <p className="text-sm text-slate-600">
                {t("I accept the", "Acepto los")}{" "}
                <Link to="/terms-and-conditions" className="text-sky-600 hover:underline" data-testid="login-terms-link">
                  {t("Terms and Conditions", "Términos y condiciones")}
                </Link>{" "}
                {t("and the", "y la")}{" "}
                <Link to="/privacy-policy" className="text-sky-600 hover:underline ml-1" data-testid="login-privacy-link">
                  {t("Privacy Policy", "Política de privacidad")}
                </Link>.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary h-11"
              disabled={loading || !acceptedPolicies}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  {t("Signing in...", "Iniciando...")}
                </span>
              ) : (
                t("Sign in", "Iniciar sesión")
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
           
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1632923565835-6582b54f2105?w=1200')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-sky-900/80 to-sky-600/70"></div>
        </div>
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <h2 className="text-3xl font-bold mb-3">
            {t("Manage your laundry efficiently", "Gestiona tu lavandería de forma eficiente")}
          </h2>
          <p className="text-sky-100 text-lg max-w-md">
            {t(
              "Complete control of customers, orders, B2B quotes and support in one platform.",
              "Control total de clientes, órdenes, cotizaciones B2B y soporte en una sola plataforma."
            )}
          </p>
          <div className="mt-8 flex gap-8">
            <div>
              <p className="text-3xl font-bold">100%</p>
              <p className="text-sky-200 text-sm">{t("Digitalized", "Digitalizado")}</p>
            </div>
            <div>
              <p className="text-3xl font-bold">24/7</p>
              <p className="text-sky-200 text-sm">{t("Available", "Disponible")}</p>
            </div>
            <div>
              <p className="text-3xl font-bold">Real-time</p>
              <p className="text-sky-200 text-sm">{t("Tracking", "Tracking")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}